import React, { useState } from 'react';
// import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import CertificatePreview from '../components/CertificatePreview';

interface MonumentStyle {
  id: string;
  name: string;
  description: string;
}

// CSS-based monument icon component
const MonumentIcon: React.FC<{ id: string }> = ({ id }) => {
  const baseClass = 'w-full h-32 flex items-center justify-center';

  switch (id) {
    case 'pillar':
      return (
        <div className={baseClass}>
          <div className="w-12 h-28 bg-gray-800 border-4 border-black shadow-lg"></div>
        </div>
      );
    case 'flame':
      return (
        <div className={baseClass}>
          <div className="relative w-16 h-28">
            <div className="absolute bottom-0 w-16 h-12 bg-gray-800 border-4 border-black"></div>
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[20px] border-l-transparent border-r-[20px] border-r-transparent border-b-[40px] border-b-[#C41E3A]"></div>
          </div>
        </div>
      );
    case 'rings':
      return (
        <div className={baseClass}>
          <div className="flex gap-[-8px]">
            <div className="w-12 h-12 rounded-full border-4 border-black bg-transparent"></div>
            <div className="w-12 h-12 rounded-full border-4 border-black bg-transparent -ml-4"></div>
            <div className="w-12 h-12 rounded-full border-4 border-black bg-transparent -ml-4"></div>
          </div>
        </div>
      );
    case 'fist':
      return (
        <div className={baseClass}>
          <div className="relative">
            <div className="w-12 h-16 bg-gray-800 border-4 border-black rounded-t-lg"></div>
            <div className="w-14 h-8 bg-gray-800 border-4 border-black border-t-0"></div>
          </div>
        </div>
      );
    case 'wings':
      return (
        <div className={baseClass}>
          <div className="flex items-center gap-2">
            <div className="w-0 h-0 border-t-[30px] border-t-transparent border-b-[30px] border-b-transparent border-r-[40px] border-r-gray-800"></div>
            <div className="w-4 h-16 bg-gray-800 border-4 border-black"></div>
            <div className="w-0 h-0 border-t-[30px] border-t-transparent border-b-[30px] border-b-transparent border-l-[40px] border-l-gray-800"></div>
          </div>
        </div>
      );
    case 'spiral':
      return (
        <div className={baseClass}>
          <div className="relative w-20 h-20">
            <div className="absolute inset-0 border-8 border-black rounded-full"></div>
            <div className="absolute inset-2 border-8 border-gray-800 rounded-full"></div>
            <div className="absolute inset-4 border-8 border-black rounded-full"></div>
          </div>
        </div>
      );
    case 'star':
      return (
        <div className={baseClass}>
          <svg className="w-20 h-20 fill-[#C41E3A] stroke-black stroke-2" viewBox="0 0 100 100">
            <polygon points="50,10 61,40 92,40 67,59 78,89 50,70 22,89 33,59 8,40 39,40" />
          </svg>
        </div>
      );
    case 'obelisk':
      return (
        <div className={baseClass}>
          <div className="flex flex-col items-center">
            <div className="w-0 h-0 border-l-[16px] border-l-transparent border-r-[16px] border-r-transparent border-b-[20px] border-b-gray-800"></div>
            <div className="w-8 h-20 bg-gray-800 border-4 border-black border-t-0"></div>
            <div className="w-12 h-4 bg-gray-800 border-4 border-black border-t-0"></div>
          </div>
        </div>
      );
    default:
      return <div className={baseClass}></div>;
  }
};

const MONUMENT_STYLES: MonumentStyle[] = [
  {
    id: 'pillar',
    name: 'The Abstract Pillar',
    description: 'Brutalist concrete column reaching skyward',
  },
  {
    id: 'flame',
    name: 'Eternal Flame',
    description: 'Modernist torch of eternal remembrance',
  },
  {
    id: 'rings',
    name: 'Unity Rings',
    description: 'Interlocking circles of brotherhood and unity',
  },
  {
    id: 'fist',
    name: 'The Fist',
    description: "Socialist realist worker's strength",
  },
  {
    id: 'wings',
    name: 'Memorial Wings',
    description: 'Abstract bird symbolizing victory and freedom',
  },
  {
    id: 'spiral',
    name: 'The Spiral',
    description: 'Brutalist spiral ascending to eternity',
  },
  {
    id: 'star',
    name: 'Star Monument',
    description: 'Five-pointed star of Yugoslav unity',
  },
  {
    id: 'obelisk',
    name: 'The Obelisk',
    description: 'Classic memorial pillar standing eternal',
  },
];

const VirtualSpomenik: React.FC = () => {
  // const { t } = useTranslation();
  const [step, setStep] = useState(1);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [selectedMonument, setSelectedMonument] = useState<string>('');
  const [formData, setFormData] = useState({
    dedicateeName: '',
    dedicationMessage: '',
    monumentName: '',
    location: '',
    purchaserName: '',
    purchaserEmail: '',
    package: 'basic',
  });

  const handleMonumentSelect = (id: string) => {
    setSelectedMonument(id);
    setStep(2);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // This would integrate with Stripe/PayPal
    // console.log('Order submitted:', { selectedMonument, ...formData });
    // Redirect to payment
  };

  return (
    <>
      <Helmet>
        <title>Virtual Spomenik Builder | Born in YU</title>
        <meta name="description" content="Create your own virtual Spomenik monument. Design and customize a brutalist monument inspired by Yugoslav memorial architecture." />
        <link rel="canonical" href="https://borninyu.com/virtual-spomenik" />
      </Helmet>
      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <div className="border-b-4 border-black bg-[#C41E3A] text-white p-8 md:p-16">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-block border-4 border-white px-4 py-2 mb-6 rotate-[-2deg]">
            <div className="text-xs font-black uppercase tracking-wider">
              Official Government Program
            </div>
          </div>
          <h1 className="text-4xl md:text-7xl font-black uppercase leading-none mb-6 tracking-tighter">
            Commission Your<br />Eternal Monument
          </h1>
          <p className="text-lg md:text-xl font-bold uppercase max-w-3xl mx-auto mb-8">
            Erect a Virtual Spomenik in the Name of Your Loved Ones, Heroes, or Yourself
          </p>
          <div className="flex flex-wrap justify-center gap-4 text-xs font-black uppercase">
            <div className="border-2 border-white px-4 py-2">✓ Official Certificate</div>
            <div className="border-2 border-white px-4 py-2">✓ Tito&apos;s Signature</div>
            <div className="border-2 border-white px-4 py-2">✓ Registry Number</div>
            <div className="border-2 border-white px-4 py-2">✓ Bureaucratic Stamps</div>
          </div>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="border-b-4 border-black bg-gray-100 py-6">
        <div className="max-w-7xl mx-auto px-8">
          <div className="flex items-center justify-center gap-4">
            <div className={`flex items-center gap-2 ${step >= 1 ? 'text-black' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full border-2 border-black flex items-center justify-center font-black ${step >= 1 ? 'bg-[#C41E3A] text-white' : 'bg-white'}`}>
                1
              </div>
              <span className="text-xs font-black uppercase hidden md:inline">Choose Monument</span>
            </div>
            <div className="w-12 h-0.5 bg-black"></div>
            <div className={`flex items-center gap-2 ${step >= 2 ? 'text-black' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full border-2 border-black flex items-center justify-center font-black ${step >= 2 ? 'bg-[#C41E3A] text-white' : 'bg-white'}`}>
                2
              </div>
              <span className="text-xs font-black uppercase hidden md:inline">Dedication</span>
            </div>
            <div className="w-12 h-0.5 bg-black"></div>
            <div className={`flex items-center gap-2 ${step >= 3 ? 'text-black' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full border-2 border-black flex items-center justify-center font-black ${step >= 3 ? 'bg-[#C41E3A] text-white' : 'bg-white'}`}>
                3
              </div>
              <span className="text-xs font-black uppercase hidden md:inline">Package</span>
            </div>
          </div>
        </div>
      </div>

      {/* Step 1: Monument Selection */}
      {step === 1 && (
        <div className="max-w-7xl mx-auto p-8 md:p-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-5xl font-black uppercase mb-4">
              Select Your Monument Style
            </h2>
            <p className="text-sm uppercase font-bold text-gray-600">
              Choose from 8 officially approved designs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {MONUMENT_STYLES.map((monument) => (
              <div
                key={monument.id}
                onClick={() => handleMonumentSelect(monument.id)}
                className="border-4 border-black bg-white cursor-pointer hover:shadow-[8px_8px_0px_0px_rgba(196,30,58,1)] transition-all group"
              >
                <div className="aspect-square bg-gray-200 border-b-4 border-black relative overflow-hidden">
                  <MonumentIcon id={monument.id} />
                </div>
                <div className="p-4">
                  <h3 className="text-lg font-black uppercase mb-2">{monument.name}</h3>
                  <p className="text-xs text-gray-600 font-bold">{monument.description}</p>
                  <button className="mt-4 w-full bg-[#C41E3A] text-white py-2 font-black uppercase text-xs border-2 border-transparent group-hover:border-black transition-all">
                    Select This Monument
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Step 2: Dedication Form */}
      {step === 2 && (
        <div className="max-w-3xl mx-auto p-8 md:p-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-5xl font-black uppercase mb-4">
              Dedication Details
            </h2>
            <p className="text-sm uppercase font-bold text-gray-600">
              Complete the official dedication form
            </p>
          </div>

          <form onSubmit={(e) => { e.preventDefault(); setStep(3); }} className="space-y-6">
            {/* Monument Name */}
            <div className="border-4 border-black p-6 bg-gray-50">
              <label className="block text-xs font-black uppercase mb-2 text-gray-500">
                Monument Name *
              </label>
              <input
                type="text"
                name="monumentName"
                value={formData.monumentName}
                onChange={handleInputChange}
                required
                placeholder="e.g., Monument to Eternal Coffee"
                className="w-full border-2 border-black px-4 py-3 font-bold text-sm uppercase focus:outline-none focus:ring-2 focus:ring-[#C41E3A]"
              />
            </div>

            {/* Dedicated To */}
            <div className="border-4 border-black p-6 bg-gray-50">
              <label className="block text-xs font-black uppercase mb-2 text-gray-500">
                Dedicated To *
              </label>
              <input
                type="text"
                name="dedicateeName"
                value={formData.dedicateeName}
                onChange={handleInputChange}
                required
                placeholder="Name of person, pet, concept, etc."
                className="w-full border-2 border-black px-4 py-3 font-bold text-sm uppercase focus:outline-none focus:ring-2 focus:ring-[#C41E3A]"
              />
            </div>

            {/* Dedication Message */}
            <div className="border-4 border-black p-6 bg-gray-50">
              <label className="block text-xs font-black uppercase mb-2 text-gray-500">
                Dedication Message * (Max 200 characters)
              </label>
              <textarea
                name="dedicationMessage"
                value={formData.dedicationMessage}
                onChange={handleInputChange}
                required
                maxLength={200}
                rows={4}
                placeholder="Your dedication message..."
                className="w-full border-2 border-black px-4 py-3 font-bold text-sm focus:outline-none focus:ring-2 focus:ring-[#C41E3A]"
              />
              <div className="text-xs text-gray-500 mt-2 text-right">
                {formData.dedicationMessage.length}/200 characters
              </div>
            </div>

            {/* Location (Optional) */}
            <div className="border-4 border-black p-6 bg-gray-50">
              <label className="block text-xs font-black uppercase mb-2 text-gray-500">
                Preferred Location (Optional)
              </label>
              <input
                type="text"
                name="location"
                value={formData.location}
                onChange={handleInputChange}
                placeholder="e.g., Belgrade, Zagreb, Sarajevo (we'll assign coordinates)"
                className="w-full border-2 border-black px-4 py-3 font-bold text-sm uppercase focus:outline-none focus:ring-2 focus:ring-[#C41E3A]"
              />
            </div>

            <div className="flex gap-4">
              <button
                type="button"
                onClick={() => setStep(1)}
                className="flex-1 bg-white border-4 border-black text-black py-4 font-black uppercase hover:bg-gray-100 transition-all"
              >
                ← Back
              </button>
              <button
                type="submit"
                className="flex-1 bg-[#C41E3A] border-4 border-black text-white py-4 font-black uppercase hover:bg-black transition-all"
              >
                Continue →
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Step 3: Package Selection */}
      {step === 3 && (
        <div className="max-w-7xl mx-auto p-8 md:p-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-5xl font-black uppercase mb-4">
              Select Your Package
            </h2>
            <p className="text-sm uppercase font-bold text-gray-600">
              All packages include official government approval
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              {/* Basic Package */}
              <div className={`border-4 ${formData.package === 'basic' ? 'border-[#C41E3A]' : 'border-black'} bg-white cursor-pointer transition-all`}
                onClick={() => setFormData({ ...formData, package: 'basic' })}>
                <div className="bg-gray-100 border-b-4 border-black p-6">
                  <h3 className="text-xl font-black uppercase mb-2">Basic Package</h3>
                  <div className="text-3xl font-black text-[#C41E3A]">$19.99</div>
                  <p className="text-xs uppercase font-bold text-gray-600 mt-2">The People&apos;s Certificate</p>
                </div>
                <div className="p-6 space-y-3">
                  <div className="flex items-start gap-2">
                    <div className="text-[#C41E3A] font-black">✓</div>
                    <span className="text-xs font-bold">Digital certificate (PDF)</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="text-[#C41E3A] font-black">✓</div>
                    <span className="text-xs font-bold">Unique registry number</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="text-[#C41E3A] font-black">✓</div>
                    <span className="text-xs font-bold">Listed on website registry</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="text-[#C41E3A] font-black">✓</div>
                    <span className="text-xs font-bold">Social media graphic</span>
                  </div>
                </div>
              </div>

              {/* Premium Package */}
              <div className={`border-4 ${formData.package === 'premium' ? 'border-[#C41E3A]' : 'border-black'} bg-white cursor-pointer transition-all relative`}
                onClick={() => setFormData({ ...formData, package: 'premium' })}>
                <div className="absolute top-0 right-0 bg-[#C41E3A] text-white px-3 py-1 text-xs font-black uppercase transform translate-x-2 -translate-y-2">
                  Popular
                </div>
                <div className="bg-black border-b-4 border-black p-6 text-white">
                  <h3 className="text-xl font-black uppercase mb-2">Premium Package</h3>
                  <div className="text-3xl font-black text-[#C41E3A]">$39.99</div>
                  <p className="text-xs uppercase font-bold text-gray-400 mt-2">Party Member&apos;s Edition</p>
                </div>
                <div className="p-6 space-y-3">
                  <div className="text-xs font-black uppercase text-gray-500 mb-2">Everything in Basic, plus:</div>
                  <div className="flex items-start gap-2">
                    <div className="text-[#C41E3A] font-black">✓</div>
                    <span className="text-xs font-bold">Printed certificate (premium paper)</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="text-[#C41E3A] font-black">✓</div>
                    <span className="text-xs font-bold">3D monument render</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="text-[#C41E3A] font-black">✓</div>
                    <span className="text-xs font-bold">Official envelope with stamps</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="text-[#C41E3A] font-black">✓</div>
                    <span className="text-xs font-bold">Dedication plaque design</span>
                  </div>
                </div>
              </div>

              {/* Deluxe Package */}
              <div className={`border-4 ${formData.package === 'deluxe' ? 'border-[#C41E3A]' : 'border-black'} bg-white cursor-pointer transition-all`}
                onClick={() => setFormData({ ...formData, package: 'deluxe' })}>
                <div className="bg-[#C41E3A] border-b-4 border-black p-6 text-white">
                  <h3 className="text-xl font-black uppercase mb-2">Deluxe Package</h3>
                  <div className="text-3xl font-black">$89.99</div>
                  <p className="text-xs uppercase font-bold text-white/80 mt-2">Supreme Soviet&apos;s Choice</p>
                </div>
                <div className="p-6 space-y-3">
                  <div className="text-xs font-black uppercase text-gray-500 mb-2">Everything in Premium, plus:</div>
                  <div className="flex items-start gap-2">
                    <div className="text-[#C41E3A] font-black">✓</div>
                    <span className="text-xs font-bold">Framed certificate (11x14&quot;)</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="text-[#C41E3A] font-black">✓</div>
                    <span className="text-xs font-bold">Metal pin badge</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="text-[#C41E3A] font-black">✓</div>
                    <span className="text-xs font-bold">Custom monument design option</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="text-[#C41E3A] font-black">✓</div>
                    <span className="text-xs font-bold">Numbered limited edition</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Your Information */}
            <div className="max-w-2xl mx-auto border-4 border-black p-8 bg-gray-50 mb-8">
              <h3 className="text-xl font-black uppercase mb-6">Your Information</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-black uppercase mb-2 text-gray-500">
                    Your Name *
                  </label>
                  <input
                    type="text"
                    name="purchaserName"
                    value={formData.purchaserName}
                    onChange={handleInputChange}
                    required
                    className="w-full border-2 border-black px-4 py-3 font-bold text-sm uppercase focus:outline-none focus:ring-2 focus:ring-[#C41E3A]"
                  />
                </div>
                <div>
                  <label className="block text-xs font-black uppercase mb-2 text-gray-500">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="purchaserEmail"
                    value={formData.purchaserEmail}
                    onChange={handleInputChange}
                    required
                    className="w-full border-2 border-black px-4 py-3 font-bold text-sm focus:outline-none focus:ring-2 focus:ring-[#C41E3A]"
                  />
                </div>
              </div>
            </div>

            <div className="max-w-2xl mx-auto flex gap-4">
              <button
                type="button"
                onClick={() => setStep(2)}
                className="flex-1 bg-white border-4 border-black text-black py-4 font-black uppercase hover:bg-gray-100 transition-all"
              >
                ← Back
              </button>
              <button
                type="submit"
                className="flex-1 bg-[#C41E3A] border-4 border-black text-white py-4 font-black uppercase hover:bg-black transition-all"
              >
                Proceed to Payment →
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Certificate Preview Section */}
      <div className="border-t-4 border-black bg-gradient-to-br from-gray-800 to-black p-8 md:p-16">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-block border-4 border-white px-4 py-2 mb-6 bg-[#C41E3A]">
              <div className="text-xs font-black uppercase tracking-wider text-white">
                Certificate Preview
              </div>
            </div>
            <h2 className="text-3xl md:text-5xl font-black uppercase mb-4 text-white">
              What You&apos;ll Receive
            </h2>
            <p className="text-sm uppercase font-bold text-gray-300 max-w-2xl mx-auto">
              An official-looking Yugoslav government certificate complete with Tito&apos;s signature, bureaucratic stamps, and unique registry number
            </p>
          </div>

          {/* Live Certificate Preview */}
          <div className="mb-8">
            <CertificatePreview
              monumentName={formData.monumentName || 'Monument Name'}
              dedicateeName={formData.dedicateeName || 'Dedicatee Name'}
              dedicationMessage={formData.dedicationMessage || 'Your dedication message will appear here...'}
              location={formData.location || 'Coordinates will be assigned'}
              showSample={!formData.monumentName && !formData.dedicateeName}
            />
          </div>

          {/* Features List */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-white">
            <div className="border-2 border-white p-6 bg-black bg-opacity-60">
              <h3 className="text-lg font-black uppercase mb-4">Included Features</h3>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-[#C41E3A] font-black">✓</span>
                  <span>Authentic Yugoslav aesthetic design</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#C41E3A] font-black">✓</span>
                  <span>Unique registry number (YU-SPO-2025-####)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#C41E3A] font-black">✓</span>
                  <span>Reproduction of Tito&apos;s signature</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#C41E3A] font-black">✓</span>
                  <span>Multiple official stamps and seals</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#C41E3A] font-black">✓</span>
                  <span>Personalized dedication message</span>
                </li>
              </ul>
            </div>
            <div className="border-2 border-white p-6 bg-black bg-opacity-60">
              <h3 className="text-lg font-black uppercase mb-4">Perfect Gift For</h3>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-[#C41E3A] font-black">•</span>
                  <span>Yugoslav history enthusiasts</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#C41E3A] font-black">•</span>
                  <span>Balkan nostalgia collectors</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#C41E3A] font-black">•</span>
                  <span>Brutalist architecture fans</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#C41E3A] font-black">•</span>
                  <span>Parents, grandparents who remember Yugoslavia</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#C41E3A] font-black">•</span>
                  <span>Unique wedding/engagement gifts</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Disclaimer Footer */}
      <div className="border-t-4 border-black bg-gray-100 p-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="text-[8px] uppercase font-bold text-gray-500 leading-relaxed">
            <p className="mb-2">
              DISCLAIMER: This is a novelty item for entertainment purposes only. No actual monument will be erected.
              The certificate is a commemorative keepsake and holds no legal authority. The Socialist Federal Republic
              of Yugoslavia no longer exists. Josip Broz Tito is deceased and did not sign this document (obviously).
              This is satire and homage to Yugoslav memorial culture.
            </p>
            <p>
              By purchasing, you acknowledge this is a virtual/symbolic dedication, similar to &quot;name a star&quot; services.
              All proceeds support the preservation of actual Yugoslav monuments through our heritage programs.
            </p>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default VirtualSpomenik;
